import React from 'react';
import { ChevronRight, Package2, Search, Truck, FileText, Shield, Clock, Users } from 'lucide-react';
import RequestForm from './services/RequestForm';
import { useLanguage } from '../contexts/LanguageContext';

function ServicesPage() {
  const { t, language } = useLanguage();

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="pt-32 pb-40 px-6 bg-gradient-to-b from-[#1B3BA0] to-[#0F2875] relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?auto=format&fit=crop&q=80')] bg-cover bg-center opacity-10"></div>
          <div className="absolute inset-0 bg-gradient-to-r from-[#1B3BA0]/50 via-[#1B3BA0]/30 to-transparent mix-blend-multiply"></div>
          <div className="absolute inset-0 bg-gradient-to-b from-transparent to-[#1B3BA0]/20"></div>
        </div>
        <div className="max-w-[980px] mx-auto relative text-center">
          <div className="max-w-[780px] mx-auto">
            <h1 className="text-5xl md:text-7xl font-bold mb-8 tracking-tight">
              <span className="text-white block mb-2">
                {language === 'ru' ? 'Комплексные решения' : 
                 language === 'en' ? 'Comprehensive Solutions' : 
                 '全面解决方案'}
              </span>
              <span className="bg-clip-text text-transparent bg-gradient-to-r from-white to-[#99BBFF]">
                {language === 'ru' ? 'для вашего бизнеса' : 
                 language === 'en' ? 'for your business' : 
                 '为您的业务'}
              </span>
            </h1>
            <p className="text-xl md:text-2xl text-white/80 leading-relaxed mx-auto">
              {language === 'ru' 
                ? 'Полный спектр услуг по работе с Китаем: от поиска поставщиков до доставки товара на ваш склад'
                : language === 'en' 
                ? 'Full range of services for working with China: from finding suppliers to delivering goods to your warehouse'
                : '与中国合作的全方位服务：从寻找供应商到将货物送达您的仓库'}
            </p>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-24 px-6 bg-white relative z-10 -mt-20">
        <div className="max-w-[980px] mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {[
              {
                icon: <Search className="w-12 h-12 text-[#1B3BA0]" />,
                title: language === 'ru' ? 'Поиск и выкуп товаров' : 
                       language === 'en' ? 'Product Sourcing' : 
                       '产品采购',
                description: language === 'ru' 
                  ? 'Профессиональный поиск поставщиков и товаров на китайских площадках. Проверка качества и надёжности производителей.'
                  : language === 'en'
                  ? 'Professional search for suppliers and products on Chinese platforms. Quality and reliability verification of manufacturers.'
                  : '在中国平台上专业搜索供应商和产品。制造商的质量和可靠性验证。',
                link: '/services/business/search',
                features: language === 'ru' 
                  ? ['Поиск поставщиков', 'Проверка производителей', 'Выкуп образцов', 'Контроль качества']
                  : language === 'en'
                  ? ['Supplier search', 'Manufacturer verification', 'Sample purchase', 'Quality control']
                  : ['供应商搜索', '制造商验证', '样品购买', '质量控制']
              },
              {
                icon: <Truck className="w-12 h-12 text-[#1B3BA0]" />,
                title: language === 'ru' ? 'Логистика и таможня' : 
                       language === 'en' ? 'Logistics and Customs' : 
                       '物流和海关',
                description: language === 'ru'
                  ? 'Комплексные решения по доставке грузов из Китая с таможенным оформлением и страхованием.'
                  : language === 'en'
                  ? 'Comprehensive solutions for cargo delivery from China with customs clearance and insurance.'
                  : '从中国交付货物的全面解决方案，包括清关和保险。',
                link: '/services/business/logistics',
                features: language === 'ru'
                  ? ['Авто доставка', 'Таможенное оформление', 'Страхование грузов', 'Складское хранение']
                  : language === 'en'
                  ? ['Auto delivery', 'Customs clearance', 'Cargo insurance', 'Warehouse storage']
                  : ['汽车运输', '海关清关', '货物保险', '仓库存储']
              },
              {
                icon: <FileText className="w-12 h-12 text-[#1B3BA0]" />,
                title: language === 'ru' ? 'Экспедирование' : 
                       language === 'en' ? 'Quality Inspection' : 
                       '质量检验',
                description: language === 'ru'
                  ? 'Контроль отгрузки и проверка качества товара перед отправкой. Инспекция производства и упаковки.'
                  : language === 'en'
                  ? 'Shipment control and product quality inspection before dispatch. Production and packaging inspection.'
                  : '发货前的装运控制和产品质量检验。生产和包装检验。',
                link: '/services/business/forwarding',
                features: language === 'ru'
                  ? ['Инспекция производства', 'Контроль качества', 'Проверка упаковки', 'Фото отчёты']
                  : language === 'en'
                  ? ['Production inspection', 'Quality control', 'Packaging verification', 'Photo reports']
                  : ['生产检验', '质量控制', '包装验证', '照片报告']
              },
              {
                icon: <Shield className="w-12 h-12 text-[#1B3BA0]" />,
                title: language === 'ru' ? 'Оплата поставщикам' : 
                       language === 'en' ? 'Payments to Suppliers' : 
                       '供应商付款',
                description: language === 'ru'
                  ? 'Безопасные способы оплаты китайским поставщикам. Защита сделок и возврат платежей при необходимости.'
                  : language === 'en'
                  ? 'Secure payment methods to Chinese suppliers. Transaction protection and payment refunds if necessary.'
                  : '向中国供应商的安全支付方式。必要时的交易保护和付款退款。',
                link: '/services/business/payment',
                features: language === 'ru'
                  ? ['Безопасные платежи', 'Возврат средств', 'Конвертация валют', 'Работа по договору']
                  : language === 'en'
                  ? ['Secure payments', 'Refunds', 'Currency conversion', 'Contract work']
                  : ['安全支付', '退款', '货币兑换', '合同工作']
              }
            ].map((service, index) => (
              <a 
                key={index}
                href={service.link}
                className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 group border border-gray-100"
              >
                <div className="bg-[#1B3BA0]/5 rounded-2xl p-4 inline-block mb-6">
                  {service.icon}
                </div>
                <h3 className="text-2xl font-semibold text-[#1d1d1f] mb-4 flex items-center group-hover:text-[#1B3BA0]">
                  {service.title}
                  <ChevronRight className="w-5 h-5 ml-2 opacity-0 group-hover:opacity-100 transform group-hover:translate-x-1 transition-all" />
                </h3>
                <p className="text-[#86868b] mb-6 leading-relaxed">
                  {service.description}
                </p>
                <ul className="space-y-2">
                  {service.features.map((feature, i) => (
                    <li key={i} className="flex items-center text-[#1d1d1f]">
                      <Clock className="w-4 h-4 text-[#1B3BA0] mr-2" />
                      {feature}
                    </li>
                  ))}
                </ul>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-32 px-6 bg-[#f5f5f7]">
        <div className="max-w-[980px] mx-auto">
          <h2 className="text-4xl font-bold text-center text-[#1B3BA0] mb-16">
            {language === 'ru' ? 'Почему выбирают нас' : 
             language === 'en' ? 'Why Choose Us' : 
             '为什么选择我们'}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                icon: <Users className="w-16 h-16 text-[#1B3BA0]" />,
                title: language === 'ru' ? 'Опытная команда' : 
                       language === 'en' ? 'Experienced Team' : 
                       '经验丰富的团队',
                description: language === 'ru'
                  ? 'Более 10 лет опыта работы с Китаем и тысячи успешных поставок'
                  : language === 'en'
                  ? 'More than 10 years of experience working with China and thousands of successful deliveries'
                  : '超过10年与中国合作的经验和数千次成功交付'
              },
              {
                icon: <Package2 className="w-16 h-16 text-[#1B3BA0]" />,
                title: language === 'ru' ? 'Полный сервис' : 
                       language === 'en' ? 'Full Service' : 
                       '全方位服务',
                description: language === 'ru'
                  ? 'От поиска товара до доставки на ваш склад — всё в одном месте'
                  : language === 'en'
                  ? 'From product search to delivery to your warehouse — everything in one place'
                  : '从产品搜索到交付到您的仓库 — 一站式服务'
              },
              {
                icon: <Shield className="w-16 h-16 text-[#1B3BA0]" />,
                title: language === 'ru' ? 'Гарантия качества' : 
                       language === 'en' ? 'Quality Guarantee' : 
                       '质量保证',
                description: language === 'ru'
                  ? 'Проверка товаров, страхование грузов и возврат платежей'
                  : language === 'en'
                  ? 'Product inspection, cargo insurance and payment refunds'
                  : '产品检验、货物保险和付款退款'
              }
            ].map((feature, index) => (
              <div key={index} className="bg-white rounded-2xl p-8 text-center shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1">
                <div className="bg-[#1B3BA0]/5 rounded-full p-6 inline-block mb-6">
                  {feature.icon}
                </div>
                <h3 className="text-xl font-bold text-[#1B3BA0] mb-4">{feature.title}</h3>
                <p className="text-[#86868b]">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Form */}
      <section className="py-32 px-6 bg-[#1B3BA0]">
        <div className="max-w-[780px] mx-auto text-center">
          <h2 className="text-4xl font-bold text-white mb-6">
            {language === 'ru' ? 'Начните работать с нами' : 
             language === 'en' ? 'Start Working With Us' : 
             '开始与我们合作'}
          </h2>
          <p className="text-xl text-white/80 mb-12 max-w-2xl mx-auto">
            {language === 'ru' 
              ? 'Оставьте заявку, и мы свяжемся с вами для обсуждения вашего проекта'
              : language === 'en' 
              ? 'Leave a request, and we will contact you to discuss your project'
              : '留下请求，我们将联系您讨论您的项目'}
          </p>
          <RequestForm source={language === 'ru' ? "Услуги" : language === 'en' ? "Services" : "服务"} />
        </div>
      </section>
    </div>
  );
}

export default ServicesPage;